import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Megaphone, 
  Users, 
  TrendingUp, 
  Target, 
  BarChart3,
  CheckCircle2,
  Send,
  Sparkles,
  Eye,
  MousePointer,
  Crown
} from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext, AppSettingsContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Advertising = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    productName: '',
    website: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.productName || !formData.website || !formData.description) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact/advertising`, formData);
      toast.success('Your inquiry has been sent! We will contact you soon.');
      setFormData({ productName: '', website: '', description: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const adPlacements = [
    {
      page: 'Dashboard',
      icon: BarChart3,
      color: 'from-blue-500 to-cyan-500',
      positions: 2,
      description: 'Maximum visibility on the first page all logged-in users see',
      traffic: 'Highest',
      price: 'Premium Tier',
      features: [
        'Displayed to all active users',
        'First page visibility',
        'High engagement rate',
        'Perfect for brand awareness'
      ]
    },
    {
      page: 'Tips Page',
      icon: Target,
      color: 'from-green-500 to-emerald-500',
      positions: 4,
      description: 'High engagement page where users browse and purchase betting tips',
      traffic: 'Very High',
      price: 'High Engagement Tier',
      features: [
        'Users actively seeking tips',
        'High purchase intent',
        'Multiple strategic placements',
        'Great for betting products'
      ]
    },
    {
      page: 'Strategies',
      icon: TrendingUp,
      color: 'from-purple-500 to-pink-500',
      positions: 4,
      description: 'Strategy-focused users exploring betting systems and methodologies',
      traffic: 'High',
      price: 'Standard Tier',
      features: [
        'Engaged betting enthusiasts',
        'Learning mindset',
        'Quality traffic',
        'Ideal for educational content'
      ]
    }
  ];

  const pricingTiers = [
    {
      name: 'Dashboard',
      tier: 'Premium',
      color: 'border-blue-500 bg-blue-500/10',
      icon: Crown,
      spaces: 2,
      features: [
        'Row 1 - Left Position: All users, maximum visibility',
        'Row 1 - Right Position: All users, maximum visibility'
      ],
      note: 'Highest traffic and brand exposure'
    },
    {
      name: 'Tips Page',
      tier: 'High Engagement',
      color: 'border-green-500 bg-green-500/10',
      icon: Target,
      spaces: 4,
      features: [
        'Ad Row 1 - Left Position: Displayed below 3rd tip',
        'Ad Row 1 - Right Position: Displayed below 3rd tip',
        'Ad Row 2 - Left Position: Displayed below 9th tip (or at bottom if fewer tips)',
        'Ad Row 2 - Right Position: Displayed below 9th tip (or at bottom if fewer tips)'
      ],
      note: 'Users with high purchase intent'
    },
    {
      name: 'Strategies',
      tier: 'Standard',
      color: 'border-purple-500 bg-purple-500/10',
      icon: TrendingUp,
      spaces: 4,
      features: [
        'Ad Row 1 - Left Position: Displayed below 3rd strategy',
        'Ad Row 1 - Right Position: Displayed below 3rd strategy',
        'Ad Row 2 - Left Position: Displayed below 9th strategy (or at bottom if fewer strategies)',
        'Ad Row 2 - Right Position: Displayed below 9th strategy (or at bottom if fewer strategies)'
      ],
      note: 'Engaged betting enthusiasts'
    }
  ];

  const benefits = [
    {
      icon: Users,
      title: 'Targeted Audience',
      description: 'Reach thousands of active bettors actively using our platform'
    },
    {
      icon: Eye,
      title: 'High Visibility',
      description: 'Premium placements across high-traffic pages'
    },
    {
      icon: MousePointer,
      title: 'Engaged Users',
      description: 'Users with high intent to purchase betting products'
    },
    {
      icon: Sparkles,
      title: 'Flexible Formats',
      description: 'Support for HTML, iFrame, and Script ad types'
    }
  ];

  // Content that will be rendered
  const pageContent = (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-8 pb-12">
        {/* Hero Section */}
        <Card className="glass-dark border-white/10 bg-gradient-to-br from-blue-900/30 via-purple-900/20 to-pink-900/20">
          <CardContent className="p-8 md:p-12">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center justify-center p-3 bg-blue-500/20 rounded-2xl mb-6">
                <Megaphone className="w-12 h-12 text-blue-400" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Advertise Your Betting Products
              </h1>
              <p className="text-xl text-gray-300 mb-6">
                Connect with thousands of active bettors and grow your business
              </p>
              <div className="flex flex-wrap items-center justify-center gap-6 text-gray-300">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span>10 Premium Ad Spaces</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span>High-Traffic Pages</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-400" />
                  <span>Targeted Audience</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Why Advertise Section */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card key={index} className="glass-dark border-white/10 hover:border-blue-500/50 transition-all">
                <CardContent className="p-6">
                  <div className="p-3 bg-blue-500/20 rounded-lg w-fit mb-4">
                    <Icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                  <p className="text-sm text-gray-400">{benefit.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Ad Placements Overview */}
        <div>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-3">Where Your Ads Appear</h2>
            <p className="text-gray-400">Strategic placements across our most popular pages</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {adPlacements.map((placement, index) => {
              const Icon = placement.icon;
              return (
                <Card key={index} className="glass-dark border-white/10 overflow-hidden group hover:border-blue-500/50 transition-all">
                  <div className={`h-2 bg-gradient-to-r ${placement.color}`} />
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`p-3 bg-gradient-to-br ${placement.color} rounded-lg`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{placement.page}</h3>
                        <p className="text-sm text-gray-400">{placement.positions} Ad Spaces</p>
                      </div>
                    </div>
                    
                    <p className="text-gray-300 text-sm mb-4">{placement.description}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-400">Traffic Level:</span>
                        <span className="text-green-400 font-semibold">{placement.traffic}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-400">Pricing:</span>
                        <span className="text-blue-400 font-semibold">{placement.price}</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2 pt-4 border-t border-white/10">
                      {placement.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Pricing Tiers */}
        <div>
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-3">Pricing Structure</h2>
            <p className="text-gray-400">Flexible options to match your advertising goals</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {pricingTiers.map((tier, index) => {
              const Icon = tier.icon;
              return (
                <Card key={index} className={`glass-dark border-2 ${tier.color}`}>
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className="w-6 h-6 text-white" />
                      <CardTitle className="text-white">{tier.name}</CardTitle>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-white">{tier.tier} Tier</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-2">{tier.spaces} available ad spaces</p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mb-4">
                      {tier.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4 border-t border-white/20">
                      <p className="text-xs text-blue-300 font-semibold">{tier.note}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Pricing Notes */}
          <Card className="glass-dark border-white/10 mt-6">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">Bundle Discounts</h4>
                  <div className="space-y-2 text-sm text-gray-300">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <span>3 ad spaces: <strong className="text-white">15% discount</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <span>6+ ad spaces: <strong className="text-white">25% discount</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      <span>Annual commitment: <strong className="text-white">Additional 20% off</strong></span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-white mb-3">What's Included</h4>
                  <div className="space-y-2 text-sm text-gray-300">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-400" />
                      <span>HTML, iFrame, and Script support</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-400" />
                      <span>Real-time ad management</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-400" />
                      <span>Performance tracking</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contact Form */}
        <Card className="glass-dark border-white/10">
          <CardHeader>
            <div className="text-center">
              <CardTitle className="text-2xl text-white mb-2">Get Started Today</CardTitle>
              <p className="text-gray-400">
                Fill out the form below and we'll contact you with pricing details and next steps
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
              <div>
                <Label className="text-gray-300 mb-2">Product/Service Name *</Label>
                <Input
                  type="text"
                  value={formData.productName}
                  onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                  placeholder="e.g., BetMaster Pro, Odds Calculator"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  required
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2">Website URL *</Label>
                <Input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://yourwebsite.com"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  required
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2">Description *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Tell us about your product/service and advertising goals. What makes your offering valuable to bettors?"
                  rows={6}
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-2">
                  Please describe your product and why it would be a good fit for our audience
                </p>
              </div>

              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-gray-300">
                  <strong className="text-white">Subject:</strong> Advertising interest
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  This inquiry will be sent to our advertising team who will review your submission 
                  and contact you with pricing details within 1-2 business days.
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-lg font-semibold"
              >
                {loading ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Submit Inquiry
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // If user is logged in, use Layout, otherwise use public navigation
  if (user) {
    return <Layout>{pageContent}</Layout>;
  }

  // Public view with Landing page-style navigation
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* Public Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-dark border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              {appSettings.app_logo_url ? (
                <img 
                  src={appSettings.app_logo_url} 
                  alt={appSettings.app_name} 
                  className="h-10 max-w-[200px] object-contain"
                />
              ) : (
                <>
                  <TrendingUp className="w-8 h-8 text-blue-400" />
                  <span className="text-lg sm:text-xl font-bold whitespace-nowrap">{appSettings.app_name}</span>
                </>
              )}
            </div>
            <Button 
              onClick={() => navigate('/login')} 
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full"
            >
              Sign In
            </Button>
          </div>
        </div>
      </nav>

      {/* Content with top padding for fixed nav */}
      <div className="pt-16">
        {pageContent}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Advertising;
